import type { GridLocaleText } from '../models/api/gridLocaleTextApi';
import { getGridLocalization, buildLocaleFormat } from '../utils/getGridLocalization';
import type { Localization } from '../utils/getGridLocalization';

const formatNumber = buildLocaleFormat('it-IT');

const itITGrid: Partial<GridLocaleText> = {
  // Root
  noRowsLabel: 'Nessun record',
  noResultsOverlayLabel: 'Nessun record trovato.',
  noColumnsOverlayLabel: 'Nessuna colonna',
  noColumnsOverlayManageColumns: 'Gestisci colonne',
  emptyPivotOverlayLabel: 'Aggiungi campi a righe, colonne e valori per creare una tabella pivot',

  // Density selector toolbar button text
  toolbarDensity: 'Densità',
  toolbarDensityLabel: 'Densità',
  toolbarDensityCompact: 'Compatta',
  toolbarDensityStandard: 'Standard',
  toolbarDensityComfortable: 'Comoda',

  // Undo/redo toolbar button text
  toolbarUndo: 'Annulla',
  toolbarRedo: 'Ripeti',

  // Columns selector toolbar button text
  toolbarColumns: 'Colonne',
  toolbarColumnsLabel: 'Seleziona le colonne',

  // Filters toolbar button text
  toolbarFilters: 'Filtri',
  toolbarFiltersLabel: 'Mostra i filtri',
  toolbarFiltersTooltipHide: 'Nascondi i filtri',
  toolbarFiltersTooltipShow: 'Mostra i filtri',
  toolbarFiltersTooltipActive: (count) =>
    count > 1 ? `${count} filtri attivi` : `${count} filtro attivo`,

  // Quick filter toolbar field
  toolbarQuickFilterPlaceholder: 'Cerca…',
  toolbarQuickFilterLabel: 'Cerca',
  toolbarQuickFilterDeleteIconLabel: 'Resetta',

  // Export selector toolbar button text
  toolbarExport: 'Esporta',
  toolbarExportLabel: 'Esporta',
  toolbarExportCSV: 'Esporta in CSV',
  toolbarExportPrint: 'Stampa',
  toolbarExportExcel: 'Scarica come Excel',

  // Toolbar pivot button
  toolbarPivot: 'Pivota',

  // Toolbar charts button
  toolbarCharts: 'Grafici',

  // Toolbar AI Assistant button
  toolbarAssistant: 'Assistente AI',

  // Formula bar (Premium formulas)
  // formulaBarLabel: 'Formula bar',
  // formulaBarInputLabel: 'Formula',
  // formulaBarAddressLabel: 'Active cell',

  // Computed columns (Premium)
  // columnMenuAddComputedColumn: 'Add computed column',
  // columnMenuEditComputedColumn: 'Edit computed column',
  // columnMenuViewComputedColumn: 'View computed column',
  // columnMenuRemoveComputedColumn: 'Remove computed column',
  // toolbarComputedColumns: 'Computed columns',
  // computedColumnsPanelTitle: 'Computed columns',
  // computedColumnsPanelCloseButton: 'Close computed columns panel',
  // computedColumnsPanelBackButton: 'Back to the list',
  // computedColumnsPanelEmpty: 'No computed columns yet',
  // computedColumnsPanelAddButton: 'Add computed column',
  // computedColumnsPanelItemMenuLabel: 'Computed column actions',
  // computedColumnsPanelItemReadOnlyLabel: 'Read-only',
  // computedColumnEditorNewTitle: 'New computed column',
  // computedColumnEditorEditTitle: 'Edit computed column',
  // computedColumnEditorViewTitle: 'View computed column',
  // computedColumnReadOnlyHint: 'This computed column cannot be edited.',
  // computedColumnNameLabel: 'Name',
  // computedColumnFieldLabel: 'Field',
  // computedColumnFormulaLabel: 'Formula',
  // computedColumnTypeLabel: 'Result type',
  // computedColumnTypeNumber: 'Number',
  // computedColumnTypeString: 'Text',
  // computedColumnTypeBoolean: 'Boolean',
  // computedColumnTypeDate: 'Date',
  // computedColumnTypeDateTime: 'Date and time',
  // computedColumnFormatLabel: 'Format',
  // computedColumnFormatStyleDecimal: 'Number',
  // computedColumnFormatStylePercent: 'Percent',
  // computedColumnFormatStyleCurrency: 'Currency',
  // computedColumnFormatCurrencyLabel: 'Currency code',
  // computedColumnFormatDecimalsLabel: 'Decimals',
  // computedColumnFormatGroupingLabel: 'Thousands separator',
  // computedColumnPreviewLabel: 'Preview',
  // computedColumnPreviewRow: rowIndex => `Row ${rowIndex}`,
  // computedColumnPreviewNoRows: 'No rows to preview',
  // computedColumnReferenceTitle: 'Insert',
  // computedColumnReferenceColumns: 'Columns',
  // computedColumnReferenceFunctions: 'Functions',
  // computedColumnReferenceSearchPlaceholder: 'Search',
  // computedColumnReferenceNoResults: 'No matches',
  // computedColumnApplyButton: 'Apply',
  // computedColumnAddButton: 'Add column',
  // computedColumnCancelButton: 'Cancel',
  // computedColumnCloseButton: 'Close',
  // computedColumnDeleteButton: 'Delete column',
  // computedColumnHeaderLabel: 'Computed column',
  // computedColumnHeaderInvalidLabel: 'Computed column with an invalid formula',
  // computedColumnErrorNameRequired: 'Enter a name.',
  // computedColumnErrorFieldRequired: 'Enter a field name.',
  // computedColumnErrorFieldInvalid: 'Use letters, digits and underscores, starting with a letter or an underscore.',
  // computedColumnErrorFieldExists: field => `A column with the field "${field}" already exists.`,
  // computedColumnErrorFieldA1Like: 'This field name reads as a cell address. Choose a longer name.',
  // computedColumnErrorFormulaRequired: 'Enter a formula.',
  // computedColumnErrorParse: message => `Formula error: ${message}`,
  // computedColumnErrorUnknownFunction: name => `Unknown function ${name}.`,
  // computedColumnErrorUnsupportedReference: 'Computed columns can only reference columns of the same row. Use bare field names.',
  // computedColumnErrorUnknownField: field => `Column "${field}" does not exist.`,
  // computedColumnErrorSelfReference: 'A computed column cannot reference itself.',
  // computedColumnErrorCycle: path => `Circular reference: ${path}.`,
  // computedCellErrorDivByZero: 'Division by zero in this row. Wrap the division in IFERROR(…, 0) to provide a fallback.',
  // computedCellErrorValue: 'A value in this row has the wrong type for this operation.',
  // computedCellErrorRef: 'A referenced column does not exist.',
  // computedCellErrorName: 'The formula uses an unknown function.',
  // computedCellErrorCycle: 'The formula depends on itself.',
  // computedCellErrorGeneric: 'The formula could not be evaluated for this row.',

  // Columns management text
  columnsManagementSearchTitle: 'Cerca',
  columnsManagementNoColumns: 'Nessuna colonna',
  columnsManagementShowHideAllText: 'Mostra/Nascondi Tutto',
  columnsManagementReset: 'Resetta',
  columnsManagementDeleteIconLabel: 'Cancella',

  // Filter panel text
  filterPanelAddFilter: 'Aggiungi un filtro',
  filterPanelRemoveAll: 'Rimuovi filtri',
  filterPanelDeleteIconLabel: 'Rimuovi',
  filterPanelLogicOperator: 'Operatore logico',
  filterPanelOperator: 'Operatori',
  filterPanelOperatorAnd: 'E (and)',
  filterPanelOperatorOr: 'O (or)',
  filterPanelColumn: 'Colonne',
  filterPanelInputLabel: 'Valore',
  filterPanelInputPlaceholder: 'Filtra il valore',

  // Filter operators text
  filterOperatorContains: 'contiene',
  filterOperatorDoesNotContain: 'non contiene',
  filterOperatorEquals: 'uguale a',
  filterOperatorDoesNotEqual: 'diverso da',
  filterOperatorStartsWith: 'comincia per',
  filterOperatorEndsWith: 'termina per',
  filterOperatorIs: 'uguale a',
  filterOperatorNot: 'diverso da',
  filterOperatorAfter: 'dopo il',
  filterOperatorOnOrAfter: 'a partire dal',
  filterOperatorBefore: 'prima del',
  filterOperatorOnOrBefore: 'fino al',
  filterOperatorIsEmpty: 'è vuoto',
  filterOperatorIsNotEmpty: 'non è vuoto',
  filterOperatorIsAnyOf: 'è uno tra',
  'filterOperator=': '=',
  'filterOperator!=': '!=',
  'filterOperator>': '>',
  'filterOperator>=': '>=',
  'filterOperator<': '<',
  'filterOperator<=': '<=',

  // Header filter operators text
  headerFilterOperatorContains: 'Contiene',
  headerFilterOperatorDoesNotContain: 'Non contiene',
  headerFilterOperatorEquals: 'Uguale a',
  headerFilterOperatorDoesNotEqual: 'Diverso da',
  headerFilterOperatorStartsWith: 'Comincia per',
  headerFilterOperatorEndsWith: 'Termina per',
  headerFilterOperatorIs: 'Uguale a',
  headerFilterOperatorNot: 'Diverso da',
  headerFilterOperatorAfter: 'Dopo il',
  headerFilterOperatorOnOrAfter: 'A partire dal',
  headerFilterOperatorBefore: 'Prima del',
  headerFilterOperatorOnOrBefore: 'Fino al',
  headerFilterOperatorIsEmpty: 'È vuoto',
  headerFilterOperatorIsNotEmpty: 'Non è vuoto',
  headerFilterOperatorIsAnyOf: 'È uno tra',
  'headerFilterOperator=': 'Uguale a',
  'headerFilterOperator!=': 'Diverso da',
  'headerFilterOperator>': 'Maggiore di',
  'headerFilterOperator>=': 'Maggiore o uguale a',
  'headerFilterOperator<': 'Minore di',
  'headerFilterOperator<=': 'Minore o uguale a',
  headerFilterClear: 'Rimuovi filtri',

  // Filter values text
  filterValueAny: 'qualunque',
  filterValueTrue: 'vero',
  filterValueFalse: 'falso',

  // Column menu text
  columnMenuLabel: 'Menu',
  columnMenuAriaLabel: (columnName: string) => `Menù della colonna ${columnName}`,
  columnMenuShowColumns: 'Mostra le colonne',
  columnMenuManageColumns: 'Gestisci colonne',
  columnMenuFilter: 'Filtra',
  columnMenuHideColumn: 'Nascondi',
  columnMenuUnsort: "Annulla l'ordinamento",
  columnMenuSortAsc: 'Ordinamento crescente',
  columnMenuSortDesc: 'Ordinamento decrescente',
  columnMenuManagePivot: 'Gestisci pivoting',
  columnMenuManageCharts: 'Gestisci grafici',

  // Column header text
  columnHeaderFiltersTooltipActive: (count) =>
    count > 1 ? `${count} filtri attivi` : `${count} filtro attivo`,
  columnHeaderFiltersLabel: 'Mostra i filtri',
  columnHeaderSortIconLabel: 'Ordina',
  columnHeaderSortIconAriaLabel: (columnName, direction) => {
    if (direction === 'asc') {
      return `Ordina la colonna ${columnName} in ordine crescente`;
    }
    if (direction === 'desc') {
      return `Ordina la colonna ${columnName} in ordine decrescente`;
    }
    return `Rimuovi l'ordinamento della colonna ${columnName}`;
  },

  // Rows selected footer text
  footerRowSelected: (count) =>
    count > 1
      ? `${count.toLocaleString()} record selezionati`
      : `${count.toLocaleString()} record selezionato`,

  // Total row amount footer text
  footerTotalRows: 'Record totali:',

  // Total visible row amount footer text
  footerTotalVisibleRows: (visibleCount, totalCount) =>
    `${visibleCount.toLocaleString()} di ${totalCount.toLocaleString()}`,

  // Checkbox selection text
  checkboxSelectionHeaderName: 'Seleziona',
  checkboxSelectionSelectAllRows: 'Seleziona tutte le righe',
  checkboxSelectionUnselectAllRows: 'Deseleziona tutte le righe',
  checkboxSelectionSelectRow: 'Seleziona riga',
  checkboxSelectionUnselectRow: 'Deseleziona riga',

  // Boolean cell text
  booleanCellTrueLabel: 'vero',
  booleanCellFalseLabel: 'falso',

  // Long text cell
  longTextCellExpandLabel: 'Espandi',
  longTextCellCollapseLabel: 'Comprimi',

  // Actions cell more text
  actionsCellMore: 'più',

  // Column pinning text
  pinToLeft: 'Blocca a sinistra',
  pinToRight: 'Blocca a destra',
  unpin: 'Sblocca',

  // Tree Data
  treeDataGroupingHeaderName: 'Gruppo',
  treeDataExpand: 'mostra figli',
  treeDataCollapse: 'nascondi figli',

  // Grouping columns
  groupingColumnHeaderName: 'Gruppo',
  groupColumn: (name) => `Raggruppa per ${name}`,
  unGroupColumn: (name) => `Annulla raggruppamento per ${name}`,

  // Master/detail
  detailPanelToggle: 'Abilita pannello dettagli',
  expandDetailPanel: 'Espandi',
  collapseDetailPanel: 'Comprimi',

  // Pagination
  paginationRowsPerPage: 'Righe per pagina:',
  paginationDisplayedRows: ({ from, to, count, estimated }) => {
    if (!estimated) {
      return `${formatNumber(from)}–${formatNumber(to)} di ${count !== -1 ? formatNumber(count) : `più di ${formatNumber(to)}`}`;
    }
    const estimatedLabel =
      estimated && estimated > to
        ? `circa ${formatNumber(estimated)}`
        : `più di ${formatNumber(to)}`;
    return `${formatNumber(from)}–${formatNumber(to)} di ${count !== -1 ? formatNumber(count) : estimatedLabel}`;
  },
  paginationItemAriaLabel: (type) => {
    if (type === 'first') {
      return 'Vai alla prima pagina';
    }
    if (type === 'last') {
      return "Vai all'ultima pagina";
    }
    if (type === 'next') {
      return 'Vai alla pagina successiva';
    }
    // if (type === 'previous') {
    return 'Vai alla pagina precedente';
  },

  // Row reordering text
  rowReorderingHeaderName: 'Riordinamento righe',

  // Aggregation
  aggregationMenuItemHeader: 'aggregazione',
  aggregationFunctionLabelNone: 'nessuna',
  aggregationFunctionLabelSum: 'somma',
  aggregationFunctionLabelAvg: 'media',
  aggregationFunctionLabelMin: 'minimo',
  aggregationFunctionLabelMax: 'massimo',
  aggregationFunctionLabelSize: 'numero di elementi',

  // Pivot panel
  pivotToggleLabel: 'Pivota la tabella',
  pivotRows: 'Righe',
  pivotColumns: 'Colonne',
  pivotValues: 'Valori',
  pivotCloseButton: 'Chiudi impostazioni di trasposizione',
  pivotSearchButton: 'Cerca attributi',
  pivotSearchControlPlaceholder: 'Cerca attributi',
  pivotSearchControlLabel: 'Cerca attributi',
  pivotSearchControlClear: 'Annulla ricerca',
  pivotNoFields: 'Nessun attributo',
  pivotMenuMoveUp: 'Sposta in alto',
  pivotMenuMoveDown: 'Sposta in basso',
  pivotMenuMoveToTop: 'Sposta in cima',
  pivotMenuMoveToBottom: 'Sposta in fondo',
  pivotMenuRows: 'Righe',
  pivotMenuColumns: 'Colonne',
  pivotMenuValues: 'Valori',
  pivotMenuOptions: 'Opzioni',
  pivotMenuAddToRows: 'Aggiungi alle Righe',
  pivotMenuAddToColumns: 'Aggiungi alle Colonne',
  pivotMenuAddToValues: 'Aggiungi ai Valori',
  pivotMenuRemove: 'Rimuovi',
  pivotDragToRows: 'Trascina qui per creare le righe',
  pivotDragToColumns: 'Trascina qui per creare le colonne',
  pivotDragToValues: 'Trascina qui per creare valori',
  pivotYearColumnHeaderName: '(Anno)',
  pivotQuarterColumnHeaderName: '(Quarto)',

  // Charts configuration panel
  chartsNoCharts: 'Non ci sono grafici disponibili',
  chartsChartNotSelected: 'Seleziona un tipo di grafico per configurarne le opzioni',
  chartsTabChart: 'Grafico',
  chartsTabFields: 'Attributi',
  chartsTabCustomize: 'Personalizza',
  chartsCloseButton: 'Chiudi la configurazione dei grafici',
  chartsSyncButtonLabel: 'Sincronizza grafico',
  chartsSearchPlaceholder: 'Cerca attributi',
  chartsSearchLabel: 'Cerca attributi',
  chartsSearchClear: 'Annulla ricerca',
  chartsNoFields: 'Nessun attributo',
  chartsFieldBlocked: 'Questo attributo non può essere aggiunto ad alcuna sezione',
  chartsCategories: 'Categorie',
  chartsSeries: 'Serie',
  chartsMenuAddToDimensions: (dimensionLabel: string) => `Aggiungi a ${dimensionLabel}`,
  chartsMenuAddToValues: (valuesLabel: string) => `Aggiungi a ${valuesLabel}`,
  chartsMenuMoveUp: 'Sposta in alto',
  chartsMenuMoveDown: 'Sposta in basso',
  chartsMenuMoveToTop: 'Sposta in cima',
  chartsMenuMoveToBottom: 'Sposta in fondo',
  chartsMenuOptions: "Opzioni per l'attributo",
  chartsMenuRemove: 'Rimuovi',
  chartsDragToDimensions: (dimensionLabel: string) =>
    `Trascina qui per usare la colonna come ${dimensionLabel}`,
  chartsDragToValues: (valuesLabel: string) =>
    `Trascina qui per usare la colonna come ${valuesLabel}`,

  // AI Assistant panel
  aiAssistantPanelTitle: 'Assistente AI',
  aiAssistantPanelClose: 'Chiudi Assistente AI',
  aiAssistantPanelNewConversation: 'Nuova Conversazione',
  aiAssistantPanelConversationHistory: 'Conversazioni precedenti',
  aiAssistantPanelEmptyConversation: 'Nessuna conversazione precedente',
  aiAssistantSuggestions: 'Suggerimenti:',

  // Prompt field
  promptFieldLabel: 'Prompt',
  promptFieldPlaceholder: 'Scrivi un prompt…',
  promptFieldPlaceholderWithRecording: 'Scrivi o registra un prompt…',
  promptFieldPlaceholderListening: 'In attesa di un prompt…',
  promptFieldSpeechRecognitionNotSupported:
    'Il riconoscimento vocale non è supportato dal tuo browser.',
  promptFieldSend: 'Invia',
  promptFieldRecord: 'Registra',
  promptFieldStopRecording: 'Interrompi registrazione',

  // Prompt
  promptRerun: 'Esegui nuovamente',
  promptProcessing: 'In elaborazione…',
  promptAppliedChanges: 'Cambiamenti applicati',

  // Prompt changes
  promptChangeGroupDescription: (column: string) => `Raggruppa per ${column}`,
  promptChangeAggregationLabel: (column: string, aggregation: string) =>
    `${column} (${aggregation})`,
  promptChangeAggregationDescription: (column: string, aggregation: string) =>
    `Aggrega ${column} (${aggregation})`,
  promptChangeFilterLabel: (
    column: string,
    operator: string,
    value: string,
    operatorValue?: string,
  ) => {
    if (operatorValue === 'isAnyOf') {
      return `${column} è uno tra: ${value}`;
    }
    return `${column} ${operator} ${value}`;
  },
  promptChangeFilterDescription: (
    column: string,
    operator: string,
    value: string,
    operatorValue?: string,
  ) => {
    if (operatorValue === 'isAnyOf') {
      return `Filtra dove ${column} è uno tra: ${value}`;
    }
    return `Filtra dove ${column} ${operator} ${value}`;
  },
  promptChangeSortDescription: (column: string, direction: string) =>
    `Ordina per ${column} (${direction})`,
  promptChangePivotEnableLabel: 'Pivota',
  promptChangePivotEnableDescription: 'Abilita pivoting',
  promptChangePivotColumnsLabel: (count: number) => `Colonne (${count})`,
  promptChangePivotColumnsDescription: (column: string, direction: string) =>
    `${column}${direction ? ` (${direction})` : ''}`,
  promptChangePivotRowsLabel: (count: number) => `Righe (${count})`,
  promptChangePivotValuesLabel: (count: number) => `Valori (${count})`,
  promptChangePivotValuesDescription: (column: string, aggregation: string) =>
    `${column} (${aggregation})`,
  promptChangeChartsLabel: (dimensionsCount: number, valuesCount: number) =>
    `Dimensioni (${dimensionsCount}), Valori (${valuesCount})`,
};

export const itIT: Localization = getGridLocalization(itITGrid);
